import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { getApiUrl } from '../config/api';
import './SorteoRuleta.css';

function SorteoRuleta() {
    const [sorteos, setSorteos] = useState([]);
    const [sorteoSeleccionado, setSorteoSeleccionado] = useState(null);
    const [participantes, setParticipantes] = useState([]);
    const [participanteActual, setParticipanteActual] = useState(null);
    const [ganador, setGanador] = useState(null);
    const [sorteando, setSorteando] = useState(false);
    const [mostrarResultado, setMostrarResultado] = useState(false);

    // Dropdown
    const [dropdownSorteoAbierto, setDropdownSorteoAbierto] = useState(false);
    const [busquedaSorteo, setBusquedaSorteo] = useState('');
    const dropdownSorteoRef = useRef(null);

    const intervalRef = useRef(null);

    useEffect(() => {
        cargarSorteos();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownSorteoRef.current && !dropdownSorteoRef.current.contains(event.target)) {
                setDropdownSorteoAbierto(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const cargarSorteos = async () => {
        try {
            const response = await axios.get(getApiUrl('/api/sorteos'));
            setSorteos(response.data);
        } catch (error) {
            console.error('Error al cargar sorteos:', error);
            alert('Error al cargar los sorteos');
        }
    };

    const cargarParticipantes = async (idSorteo) => {
        try {
            const response = await axios.get(getApiUrl(`/api/sorteos-participantes/sorteo/${idSorteo}/disponibles`));
            setParticipantes(response.data);

            if (response.data.length === 0) {
                alert('No hay participantes disponibles para este sorteo');
            }
        } catch (error) {
            console.error('Error al cargar participantes:', error);
            alert('Error al cargar los participantes');
        }
    };

    const filtrarSorteos = (busq) => {
        if (!busq) return sorteos;
        return sorteos.filter(s =>
            s.idSorteo?.toString().includes(busq) ||
            s.nomSorteo?.toLowerCase().includes(busq.toLowerCase())
        );
    };

    const seleccionarSorteo = (sorteo) => {
        setSorteoSeleccionado(sorteo);
        setBusquedaSorteo('');
        setDropdownSorteoAbierto(false);
        setGanador(null);
        setMostrarResultado(false);
        cargarParticipantes(sorteo.idSorteo);
    };

    const iniciarSorteo = () => {
        if (!sorteoSeleccionado) {
            alert('Debe seleccionar un sorteo');
            return;
        }

        if (participantes.length === 0) {
            alert('No hay participantes disponibles para sortear');
            return;
        }

        setSorteando(true);
        setMostrarResultado(false);
        setGanador(null);

        let contador = 0;
        const duracion = 5000; // 5 segundos
        const intervalo = 10; // 10 ms
        const iteraciones = duracion / intervalo; // 500 iteraciones

        intervalRef.current = setInterval(() => {
            // Seleccionar participante aleatorio
            const indiceAleatorio = Math.floor(Math.random() * participantes.length);
            setParticipanteActual(participantes[indiceAleatorio]);

            contador++;

            if (contador >= iteraciones) {
                clearInterval(intervalRef.current);

                // Seleccionar ganador final
                const indiceGanador = Math.floor(Math.random() * participantes.length);
                const ganadorFinal = participantes[indiceGanador];

                setGanador(ganadorFinal);
                setParticipanteActual(null);
                setSorteando(false);
                setMostrarResultado(true);
            }
        }, intervalo);
    };

    const marcarGanador = async () => {
        if (!ganador) return;

        try {
            await axios.put(getApiUrl(`/api/sorteos-participantes/${ganador.idSorteo}/${ganador.id}/marcar`));
            alert('¡Ganador marcado exitosamente!');

            // Recargar participantes
            cargarParticipantes(sorteoSeleccionado.idSorteo);
            setGanador(null);
            setMostrarResultado(false);
        } catch (error) {
            console.error('Error al marcar ganador:', error);
            alert('Error al marcar el ganador');
        }
    };

    const reiniciarSorteo = () => {
        setGanador(null);
        setMostrarResultado(false);
        setParticipanteActual(null);
        if (sorteoSeleccionado) {
            cargarParticipantes(sorteoSeleccionado.idSorteo);
        }
    };

    const exportarGanadoresExcel = async () => {
        if (!sorteoSeleccionado) {
            alert('Debe seleccionar un sorteo');
            return;
        }

        try {
            const response = await axios.get(getApiUrl(`/api/sorteos-participantes/sorteo/${sorteoSeleccionado.idSorteo}/ganadores`));
            const ganadores = response.data;

            if (ganadores.length === 0) {
                alert('No hay ganadores registrados para este sorteo');
                return;
            }

            const datosExportar = ganadores.map(ganador => ({
                'Sorteo': sorteoSeleccionado.nomSorteo,
                'ID Sorteo': sorteoSeleccionado.idSorteo,
                'Código Participante': ganador.codParticipante,
                'Nombre': ganador.nomParticipante,
                'Teléfonos': ganador.telefonos,
                'Observaciones': ganador.obs
            }));

            const worksheet = XLSX.utils.json_to_sheet(datosExportar);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Ganadores');
            XLSX.writeFile(workbook, `ganadores_${sorteoSeleccionado.nomSorteo.replace(/\s+/g, '_')}.xlsx`);
        } catch (error) {
            console.error('Error al exportar ganadores:', error);
            alert('Error al exportar los ganadores a Excel');
        }
    };

    return (
        <div className="sorteo-container">
            <div className="sorteo-header">
                <h1>🎰 Sorteo de Participantes</h1>
            </div>

            <div className="sorteo-selector">
                <label>Seleccionar Sorteo:</label>
                <div className="cuenta-selector" ref={dropdownSorteoRef}>
                    {sorteoSeleccionado ? (
                        <div className="cuenta-seleccionada">
                            <span>{sorteoSeleccionado.idSorteo} - {sorteoSeleccionado.nomSorteo}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    setSorteoSeleccionado(null);
                                    setParticipantes([]);
                                    setGanador(null);
                                    setMostrarResultado(false);
                                }}
                                className="btn-limpiar"
                            >
                                ✕
                            </button>
                        </div>
                    ) : (
                        <input
                            type="text"
                            placeholder="Buscar sorteo..."
                            value={busquedaSorteo}
                            onChange={(e) => setBusquedaSorteo(e.target.value)}
                            onFocus={() => setDropdownSorteoAbierto(true)}
                            className="input-busqueda"
                        />
                    )}
                    {dropdownSorteoAbierto && !sorteoSeleccionado && (
                        <div className="cuenta-dropdown">
                            {filtrarSorteos(busquedaSorteo).map(sorteo => (
                                <div
                                    key={sorteo.idSorteo}
                                    className="cuenta-item"
                                    onClick={() => seleccionarSorteo(sorteo)}
                                >
                                    <strong>{sorteo.idSorteo}</strong> - {sorteo.nomSorteo}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {sorteoSeleccionado && (
                    <div className="participantes-info">
                        <p>Participantes disponibles: <strong>{participantes.length}</strong></p>
                    </div>
                )}
            </div>

            {sorteoSeleccionado && participantes.length > 0 && (
                <div className="sorteo-controles">
                    <button
                        onClick={iniciarSorteo}
                        disabled={sorteando || mostrarResultado}
                        className="btn-sortear"
                    >
                        🎲 Iniciar Sorteo
                    </button>

                    {mostrarResultado && (
                        <button
                            onClick={reiniciarSorteo}
                            className="btn-reiniciar"
                        >
                            🔄 Nuevo Sorteo
                        </button>
                    )}

                    <button
                        onClick={exportarGanadoresExcel}
                        className="btn-exportar-ganadores"
                    >
                        📊 Exportar Ganadores
                    </button>
                </div>
            )}

            {/* Área de animación */}
            {sorteando && participanteActual && (
                <div className="sorteo-animacion">
                    <div className="participante-rotando">
                        <div className="participante-nombre-animado">
                            {participanteActual.nomParticipante}
                        </div>
                        <div className="participante-codigo-animado">
                            {participanteActual.codParticipante}
                        </div>
                    </div>
                    <div className="sorteo-spinner">
                        <div className="spinner"></div>
                    </div>
                </div>
            )}

            {/* Resultado del sorteo */}
            {mostrarResultado && ganador && (
                <div className="sorteo-resultado">
                    <div className="resultado-header">
                        <h2>🎉 ¡GANADOR! 🎉</h2>
                    </div>

                    <div className="ganador-info">
                        <div className="ganador-campo">
                            <label>Código:</label>
                            <div className="ganador-valor grande">{ganador.codParticipante}</div>
                        </div>

                        <div className="ganador-campo">
                            <label>Nombre:</label>
                            <div className="ganador-valor grande destacado">{ganador.nomParticipante}</div>
                        </div>

                        <div className="ganador-campo">
                            <label>Teléfono:</label>
                            <div className="ganador-valor">{ganador.telefonos}</div>
                        </div>

                        <div className="ganador-campo">
                            <label>Observaciones:</label>
                            <div className="ganador-valor">{ganador.obs}</div>
                        </div>
                    </div>

                    <div className="resultado-acciones">
                        <button
                            onClick={marcarGanador}
                            className="btn-marcar"
                        >
                            ✓ Marcar como Ganador
                        </button>
                    </div>
                </div>
            )}

            {/* Mensaje si no hay sorteo seleccionado */}
            {!sorteoSeleccionado && (
                <div className="sorteo-vacio">
                    <div className="icono-sorteo">🎰</div>
                    <p>Selecciona un sorteo para comenzar</p>
                </div>
            )}
        </div>
    );
}

export default SorteoRuleta;
